/**
 * The party identity strip on the canonical record (#2267 port 5).
 *
 * Mounted from `RecordBody`, so it reaches the record PAGE and the split pane
 * alike — the legacy `OrgRolePill` lived on the CRM route only, which is half
 * the reason the same organisation looked like two different records depending
 * on where you opened it.
 *
 * `partyIdentityBadges` owns every decision about what is shown and in what
 * order; this component owns the reads and the three states they can be in.
 * Keeping them apart is what lets the ordering, the de-duplication and the
 * catalogue fallback be proven without a client.
 *
 * ── the three states, and why the middle one is not silence ─────────────────
 *   · roles read, none active   → nothing. A party with no commercial role is
 *     an ordinary record, not an error, and an empty strip beside the title is
 *     chrome that says nothing.
 *   · roles REFUSED or failed   → a neutral mark saying the roles could not be
 *     read. Rendering nothing here would be indistinguishable from "wears no
 *     roles", and on a record whose whole point is that it is a customer that
 *     is a wrong answer rather than a missing one (#389's rule, on the strip).
 *   · roles read, some active   → the marks.
 *
 * A LOADING read is silent on purpose: the strip appears beside the heading, and
 * flashing a skeleton there moves the record's own name on every open.
 */
import { Pill } from '@solum/ui'
import type { Row } from '@solum/sdk'
import { useRelated } from '../data/queries'
import { isPartyEntity } from './partyEntities'
import { partyIdentityBadges } from './partyIdentityBadges'

const asRows = (data: unknown): Row[] => (Array.isArray(data) ? (data as Row[]) : [])

export function RecordIdentityBadges({ entity, id }: { entity: string; id: string }) {
  const party = isPartyEntity(entity) && id !== 'new'
  /* UNFILTERED (#1075): an archived role type still has to spell an assignment
     that already carries it. See `partyIdentityBadges`. */
  const roleTypes = useRelated('core', 'party_role_type', {}, party)
  const roles = useRelated('core', 'party_role', { party_id: id }, party)

  if (!party) return null
  if (roles.isLoading || roleTypes.isLoading) return null
  if (roles.isError) {
    return (
      <span data-testid="record-roles-unavailable">
        <Pill tone="neutral">Roles unavailable</Pill>
      </span>
    )
  }

  /* A refused CATALOGUE is not a refused assignment: the roles themselves read,
     so they are shown under their keys rather than withheld. Same argument as
     the unknown-key fallback inside `partyIdentityBadges` — a governed value
     appearing raw is a registration somebody fixes; one that vanishes is a
     record that reads as less than it is. */
  const badges = partyIdentityBadges(asRows(roles.data), roleTypes.isError ? [] : asRows(roleTypes.data))
  if (badges.length === 0) return null

  return (
    <>
      {badges.map((badge) => (
        /* The wrapper carries the identity and the TONE. `Pill` renders tone as
           a CSS-module class, which is a hashed string in a test and a colour on
           screen — neither is a readable statement of "this tone came from the
           catalogue". `data-tone` is, and it is the thing the mutation arm for a
           hardcoded tone map has to be able to see. */
        <span key={badge.key} data-testid={`record-role-${badge.key}`} data-tone={badge.tone}>
          <Pill tone={badge.tone}>{badge.label}</Pill>
        </span>
      ))}
    </>
  )
}
